import { SimilarTagType } from "@/apis/taxonomy/types";
import SearchResultItem from "@/components/common/search-result-item";
import queries from "@/queries";
import { Dimension } from "@/types/mapping";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";

const SimilarTagsSection = ({
  form,
  conflictTag,
  selectedExistingTag,
  setSelectedExistingTag,
  workbookId,
  dimension,
  koLabel,
  setKoLabel,
  debouncedKoLabel,
}: {
  form: UseFormReturn<any>;
  conflictTag: SimilarTagType | null;
  selectedExistingTag: SimilarTagType | null;
  setSelectedExistingTag: (selectedExistingTag: SimilarTagType | null) => void;
  workbookId: string;
  dimension: Dimension;
  koLabel: string;
  setKoLabel: (koLabel: string) => void;
  debouncedKoLabel: string;
}) => {
  const { data: extendedTags, isFetching } = useQuery({
    ...queries
      .workbook(workbookId!)
      ._ctx.tags()
      ._ctx.similarTags(debouncedKoLabel, dimension),
    enabled: !!debouncedKoLabel && !!workbookId && !selectedExistingTag,
    placeholderData: (previousData) => previousData,
  });

  const shownSimilarTags = useMemo(() => {
    const tags: SimilarTagType[] = [];
    const tagToPush = conflictTag;
    if (tagToPush) {
      tags.push(tagToPush);
    }
    if (extendedTags?.tags) {
      tags.push(
        ...extendedTags?.tags.filter((tag) => tag.name !== tagToPush?.name),
      );
    }

    return tags;
  }, [extendedTags, conflictTag, selectedExistingTag]);

  return (
    <>
      {(debouncedKoLabel !== "" || koLabel !== "") && (
        <div className="flex flex-col gap-2">
          <div className="mx-4 bg-neutral-50 pb-2 rounded-[8px]">
            <div className="py-3 px-4 caption-1-semibold">유사 태그</div>
            <div className="flex flex-col px-4 min-h-[168px] max-h-[250px] overflow-y-auto items-center gap-2">
              {isFetching && (
                <div className="flex items-center justify-center w-full min-h-[168px] max-h-[250px]">
                  <Loader2 className="w-[24px] h-[24px] flex-shrink-0 aspect-square animate-spin" />
                </div>
              )}
              {!isFetching &&
                shownSimilarTags &&
                (shownSimilarTags.length == 0 ? (
                  <div className="flex items-center justify-center w-full min-h-[168px] max-h-[250px] label-1-normal-medium text-neutral-500">
                    유사한 태그가 없습니다.
                  </div>
                ) : (
                  shownSimilarTags.map((tag) => (
                    <SearchResultItem
                      key={tag.id}
                      id={tag.id}
                      isStandard={tag.prefix !== "entity"}
                      isSum={false}
                      isSelected={selectedExistingTag?.id === tag.id}
                      innerClassName="bg-neutral-50"
                      label={tag.koLabel}
                      prefix={tag.prefix}
                      name={tag.name}
                      tagType={tag.type}
                      isSameTag={tag.name === conflictTag?.name}
                      handleClick={async () => {
                        setSelectedExistingTag(tag);
                        setKoLabel(tag.koLabel);
                        form.setValue("koLabel", tag.koLabel);
                        form.setValue("enLabel", tag.enLabel);
                      }}
                    />
                  ))
                ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SimilarTagsSection;
